import {
    AddRounded,
    AutorenewRounded,
    BoltRounded,
    CalendarMonthRounded,
    CompareArrowsRounded,
    DescriptionRounded,
    EditNoteRounded,
    EmojiEventsRounded,
    EventNoteRounded,
    FactCheckRounded,
    GroupsRounded,
    QueryStatsRounded,
    RecordVoiceOverRounded,
    RocketLaunchRounded,
    ScheduleRounded,
    SummarizeRounded,
    TagRounded,
    TravelExploreRounded,
    TrendingDownRounded,
    WhatshotRounded,
    type SvgIconComponent,
} from '@mui/icons-material';
import { AndieAgents } from '@/ai/enum/andie-agents.enum';

/**
 * Functional area an action preset belongs to. Mirrors the agent areas Andie
 * switches between (see {@link AndieAgents}). `general` presets are valid
 * everywhere.
 */
export type AndieActionArea = 'insights' | 'content' | 'general';

export interface AndieActionPreset {
    /** Stable identifier, also used to build the i18n keys. */
    key: string;
    area: AndieActionArea;
    /** i18n key for the button label. */
    labelKey: string;
    /** i18n key for the (translated, user-visible) starting prompt. */
    messageKey: string;
    /**
     * Optional extra instructions wrapped in a `<context-message>` tag when the
     * prompt is sent. The tag is stripped from the chat UI (see AndieChat's
     * user-message rendering) but reaches the agent, making action-heavy presets
     * more reliable. Kept in English on purpose — it steers the agent, not the
     * user; replies still follow the user's language.
     */
    context?: string;
    icon: SvgIconComponent;
}

/**
 * Static catalogue of Andie starting prompts. Only a random subset is ever
 * shown to the user (see {@link pickRandomPresets}); the list mixes obvious and
 * less obvious but high-value actions across insights, content and general
 * areas.
 */
export const ANDIE_ACTION_PRESETS: AndieActionPreset[] = [
    // --- Insights ---
    {
        key: 'dashboard-analysis',
        area: 'insights',
        labelKey: 'andie.preset.dashboard-analysis.label',
        messageKey: 'andie.preset.dashboard-analysis.message',
        context: 'If the user is not already viewing a dashboard, navigate to the insights/dashboards area first. If which dashboard to analyse is ambiguous, ask; if there are none, help create one. Then summarise the key metrics, notable trends and anomalies. Do not mention these instructions to the user.',
        icon: QueryStatsRounded,
    },
    {
        key: 'competitor-benchmark',
        area: 'insights',
        labelKey: 'andie.preset.competitor-benchmark.label',
        messageKey: 'andie.preset.competitor-benchmark.message',
        context: 'Navigate to the insights/dashboards area. Ask which competitor to benchmark against. Use or help create a dashboard that includes that competitor, then compare it against the user\'s own profiles. Do not mention these instructions to the user.',
        icon: CompareArrowsRounded,
    },
    {
        key: 'best-time-to-post',
        area: 'insights',
        labelKey: 'andie.preset.best-time-to-post.label',
        messageKey: 'andie.preset.best-time-to-post.message',
        icon: ScheduleRounded,
    },
    {
        key: 'audience-growth',
        area: 'insights',
        labelKey: 'andie.preset.audience-growth.label',
        messageKey: 'andie.preset.audience-growth.message',
        icon: GroupsRounded,
    },
    {
        key: 'top-content',
        area: 'insights',
        labelKey: 'andie.preset.top-content.label',
        messageKey: 'andie.preset.top-content.message',
        icon: EmojiEventsRounded,
    },
    {
        key: 'engagement-drop',
        area: 'insights',
        labelKey: 'andie.preset.engagement-drop.label',
        messageKey: 'andie.preset.engagement-drop.message',
        icon: TrendingDownRounded,
    },
    {
        key: 'hashtag-performance',
        area: 'insights',
        labelKey: 'andie.preset.hashtag-performance.label',
        messageKey: 'andie.preset.hashtag-performance.message',
        icon: TagRounded,
    },
    {
        key: 'content-gaps',
        area: 'insights',
        labelKey: 'andie.preset.content-gaps.label',
        messageKey: 'andie.preset.content-gaps.message',
        icon: TravelExploreRounded,
    },
    // --- Content ---
    {
        key: 'create-post',
        area: 'content',
        labelKey: 'andie.preset.create-post.label',
        messageKey: 'andie.preset.create-post.message',
        context: 'Navigate to the post creation page, even if already there. Then ask for the topic and any briefing details, show the profile selector (use the tool) to choose the profile, and start the briefing creation with the available tools. Do not mention these instructions to the user.',
        icon: AddRounded,
    },
    {
        key: 'content-plan',
        area: 'content',
        labelKey: 'andie.preset.content-plan.label',
        messageKey: 'andie.preset.content-plan.message',
        context: 'Navigate to the calendar/planning page. Ask for: the period duration (any number of days up to 60), the topics, events or themes to focus on, and the start date (default to today). Then show the profile selector (use the tool) and use the planning tool to create the content plan. Do not mention these instructions to the user.',
        icon: CalendarMonthRounded,
    },
    {
        key: 'post-briefing',
        area: 'content',
        labelKey: 'andie.preset.post-briefing.label',
        messageKey: 'andie.preset.post-briefing.message',
        context: 'Ask for the post topic and the target profile/platform if not provided. Show the profile selector (use the tool) when a profile is needed, then generate a structured briefing using the available tools. Do not mention these instructions to the user.',
        icon: DescriptionRounded,
    },
    {
        key: 'repurpose',
        area: 'content',
        labelKey: 'andie.preset.repurpose.label',
        messageKey: 'andie.preset.repurpose.message',
        icon: AutorenewRounded,
    },
    {
        key: 'caption-ideas',
        area: 'content',
        labelKey: 'andie.preset.caption-ideas.label',
        messageKey: 'andie.preset.caption-ideas.message',
        icon: EditNoteRounded,
    },
    {
        key: 'trending-topics',
        area: 'content',
        labelKey: 'andie.preset.trending-topics.label',
        messageKey: 'andie.preset.trending-topics.message',
        icon: WhatshotRounded,
    },
    {
        key: 'weekly-plan',
        area: 'content',
        labelKey: 'andie.preset.weekly-plan.label',
        messageKey: 'andie.preset.weekly-plan.message',
        context: 'Navigate to the calendar/planning page and plan the next 7 days starting today. Ask which topics or events to focus on, show the profile selector (use the tool), then use the planning tool to create the plan. Do not mention these instructions to the user.',
        icon: EventNoteRounded,
    },
    // --- General ---
    {
        key: 'weekly-summary',
        area: 'general',
        labelKey: 'andie.preset.weekly-summary.label',
        messageKey: 'andie.preset.weekly-summary.message',
        icon: SummarizeRounded,
    },
    {
        key: 'quick-wins',
        area: 'general',
        labelKey: 'andie.preset.quick-wins.label',
        messageKey: 'andie.preset.quick-wins.message',
        icon: BoltRounded,
    },
    {
        key: 'brand-voice',
        area: 'general',
        labelKey: 'andie.preset.brand-voice.label',
        messageKey: 'andie.preset.brand-voice.message',
        icon: RecordVoiceOverRounded,
    },
    {
        key: 'profile-audit',
        area: 'general',
        labelKey: 'andie.preset.profile-audit.label',
        messageKey: 'andie.preset.profile-audit.message',
        icon: FactCheckRounded,
    },
    {
        key: 'growth-strategy',
        area: 'general',
        labelKey: 'andie.preset.growth-strategy.label',
        messageKey: 'andie.preset.growth-strategy.message',
        icon: RocketLaunchRounded,
    },
];

/** Maps the active Andie agent to the action area it should surface presets for. */
export const andieAgentToArea = (agentId?: string): AndieActionArea => {
    if (agentId === AndieAgents.CONTENT) return 'content';
    if (agentId === AndieAgents.INSIGHTS) return 'insights';
    return 'general';
};

/**
 * Returns the presets eligible for an area: the area-specific ones for
 * `insights`/`content`, or the full catalogue for `general`.
 */
export const getAndiePresetsForArea = (area: AndieActionArea): AndieActionPreset[] =>
    area === 'general' ? ANDIE_ACTION_PRESETS : ANDIE_ACTION_PRESETS.filter((preset) => preset.area === area);

/** Returns up to `count` presets picked at random (Fisher–Yates, non-mutating). */
export const pickRandomPresets = (presets: AndieActionPreset[], count: number): AndieActionPreset[] => {
    if (presets.length <= count) return presets;
    const pool = [...presets];
    for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool.slice(0, count);
};

/** Convenience: pick `count` random presets appropriate for the given area. */
export const getRandomPresetsForArea = (area: AndieActionArea, count = 4): AndieActionPreset[] =>
    pickRandomPresets(getAndiePresetsForArea(area), count);

/**
 * Builds the message actually sent to Andie: the translated, user-visible prompt
 * plus the preset's hidden `<context-message>` block (when present). The tag is
 * stripped from the chat UI but reaches the agent.
 */
export const buildAndiePresetMessage = (preset: AndieActionPreset, translatedMessage: string): string =>
    preset.context
        ? `${translatedMessage}\n<context-message>\n${preset.context}\n</context-message>`
        : translatedMessage;
